/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect } from 'react';

import {
    AppDispatch,
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../core/tivo/tivo-redux-wrapper';
import { removeListEntry, setListEntry } from '../../../store/list/list.actions';
import { ListItemType, ListProps } from './ListProps';

import './List.scss';

const List: FunctionComponent<ListProps> = (props): JSX.Element => {
    const {
        type,
        itemList,
        defaultItem,
        generateItemView,
        initialSelectedIndex,
        initialFocusedIndex,
        isHorizontalList = false,
        classStyle,
    } = props;

    const dispatch: AppDispatch = useTivoDispatch();
    const listEntry = useTivoSelector((state: RootState) => state.list[type]);
    const position = listEntry?.position;
    const isListFocused = listEntry?.isFocused;

    const generateItemClass = (item: ListItemType, index: number): string => {
        const itemClassList: string[] = [];

        if (item.isDisabled) itemClassList.push('disabled');

        if (position === undefined) {
            if (initialSelectedIndex === index) itemClassList.push('selected');
            if (initialFocusedIndex === index) itemClassList.push('focused');
        } else if (position === index) {
            itemClassList.push('selected');
            if (isListFocused) itemClassList.push('focused');
        }

        return itemClassList.join(' ');
    };

    const generateItem = (
        item: any,
        itemClass?: any,
        index?: number,
        horizontalIndex?: number,
    ): JSX.Element => {
        if (defaultItem) {
            switch (defaultItem) {
                case 'type1':
                    return <span className="title">{item}</span>;
                // TODO : Add other common item type here as per requirement
                case 'type2':
                    return <></>;
                default:
                    return <></>;
            }
        } else if (generateItemView) {
            return generateItemView(item, itemClass, index, horizontalIndex, position);
        }
        return <></>;
    };

    useEffect(() => {
        dispatch(
            setListEntry(type, {
                position: position ?? initialSelectedIndex ?? initialFocusedIndex ?? 0,
                limit: itemList.length,
                isFocused: isListFocused ?? initialFocusedIndex !== undefined,
            }),
        );
    }, [itemList, initialSelectedIndex, initialFocusedIndex]);

    useEffect(() => {
        return () => {
            dispatch(removeListEntry(type));
        };
    }, []);

    return (
        <div
            className={`list-container ${isHorizontalList ? 'list-horizontal' : 'list-vertical'} ${type} ${classStyle}`}
        >
            {itemList.map((item, index) => {
                const itemClass = generateItemClass(item, index);

                return generateItem(item, itemClass, index);
            })}
        </div>
    );
};

export default List;
